// ReleveExternePrintUtility.js
import React from 'react';
import axios from 'axios';
import { Modal, Spin, Button } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import ReactDOM from 'react-dom';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const API_URL = `${process.env.REACT_APP_API_BASE_URL}`;

const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
};

const formatNumber = (num) => {
    const numValue = Number(num) || 0;
    return numValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const formatFullDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const days = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
    const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    const dayName = days[d.getDay()];
    const day = d.getDate();
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return `${dayName} ${day} ${month} ${year}`;
};

// Template for relevé externe
const releveExterneTemplate = `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <title>Relevé Externe</title>
    <style>
        @page {
            size: A5 landscape;
            margin: 1cm;
        }
        body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
            font-size: 11px;
            line-height: 1.3;
        }
        .header {
            margin-bottom: 15px;
        }
        .company-name {
            font-weight: bold;
            font-size: 14px;
            margin-bottom: 5px;
            text-align: left;
        }
        .title {
            text-align: center;
            font-weight: bold;
            font-size: 16px;
            margin: 5px 0 10px 0;
        }
        .subtitle {
            text-align: center;
            font-size: 12px;
            margin-bottom: 5px;
        }
        .client-info-row {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 10px;
        }
        .client-name {
            font-weight: bold;
            font-size: 13px;
            text-align: left;
        }
        .date-info {
            text-align: right;
            font-size: 11px;
        }
        .page-info {
            position: absolute;
            top: 0;
            right: 0;
            font-size: 10px;
            padding: 5px;
        }
        .header {
            margin-bottom: 15px;
            position: relative;
        }
        .section-title {
            font-weight: bold;
            font-size: 13px;
            margin-top: 15px;
            margin-bottom: 8px;
            padding: 5px;
            background-color: #f0f0f0;
            border: 1px solid #000;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 10px;
        }
        table th, table td {
            border: none;
            padding: 4px;
            text-align: left;
        }
        table th {
            background-color: #f0f0f0;
            font-weight: bold;
            text-align: center;
            font-size: 10px;
        }
        table td {
            font-size: 11px;
        }
        /* Header row with bottom border only for visual separation */
        table thead tr {
            border-bottom: 2px solid #000;
        }
        /* Subtle separators between data rows */
        table tbody tr {
            border-bottom: 1px solid #f0f0f0;
        }
        .reference-col {
            width: 20%;
        }
        .date-col {
            width: 15%;
        }
        .mnt-ttc-col {
            width: 22%;
            text-align: right;
        }
        .mnt-regler-col {
            width: 22%;
            text-align: right;
        }
        .solde-col {
            width: 21%;
            text-align: right;
        }
        .text-right {
            text-align: right;
        }
        .total-row {
            font-weight: bold;
            background-color: #e8e8e8;
        }
        .grand-total-row {
            font-weight: bold;
            background-color: #d0d0d0;
            font-size: 12px;
            border-top: 2px solid #000;
            border-bottom: none;
        }
        @media print {
            body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
        }
    </style>
</head>
<body>
    <div class="header">
        <div class="page-info">Page {{PAGE_NUM}} sur {{TOTAL_PAGES}}</div>
        <div class="company-name">IHSSAN PIECES AUTO</div>
        <div class="title">Relevé de compte clients</div>
        <div class="subtitle">Facture à encaisser</div>
        <div class="client-info-row">
            <div class="client-name">{{CLIENT_NAME}}</div>
            <div class="date-info">{{CURRENT_DATE}}</div>
        </div>
    </div>
    
    {{SECTIONS}}
</body>
</html>`;

const processTemplate = (template, replacements) => {
    let processedTemplate = template;
    Object.entries(replacements).forEach(([key, value]) => {
        const regex = new RegExp(`{{${key}}}`, 'g');
        processedTemplate = processedTemplate.replace(regex, value || '');
    });
    return processedTemplate;
};

// Helper function to get DATEVALID from a record, fallback to CreatedAt
const getDateValid = (record) => {
    if (record.facture && record.facture.DATEVALID) {
        return record.facture.DATEVALID;
    } else if (record.avoir && record.avoir.DATEVALID) {
        return record.avoir.DATEVALID;
    } else if (record.surplus && record.surplus.DATE_SURPLUS) {
        return record.surplus.DATE_SURPLUS;
    }
    // Fallback to CreatedAt if no DATEVALID found
    return record.CreatedAt || null;
};

const generateReleveSections = (unifiedRecords, clientInfo) => {
    // Filter records where MNT_TTC != MNT_REGLER (not fully settled)
    const unpaidRecords = unifiedRecords.filter(record => {
        const mntTtc = record.MNT_TTC || 0;
        const mntRegler = record.MNT_REGLER || 0;
        // Check if not fully settled (accounting for negative values)
        return mntTtc !== mntRegler;
    });

    // Sort records by DATEVALID (oldest first)
    unpaidRecords.sort((a, b) => {
        const dateA = getDateValid(a);
        const dateB = getDateValid(b);
        const dateAValue = dateA ? new Date(dateA) : new Date(0);
        const dateBValue = dateB ? new Date(dateB) : new Date(0);
        return dateAValue - dateBValue;
    });

    let rowsHtml = '';
    let grandTotalMntTtc = 0;
    let grandTotalMntRegler = 0;
    let grandTotalSolde = 0;

    // Generate rows for all records in one table
    unpaidRecords.forEach(record => {
        // Use raw values - avoir and surplus are already negative
        const mntTtc = record.MNT_TTC || 0;
        const mntRegler = record.MNT_REGLER || 0;
        const solde = mntTtc - mntRegler;

        grandTotalMntTtc += mntTtc;
        grandTotalMntRegler += mntRegler;
        grandTotalSolde += solde;

        // Get reference from related records
        let ref = record.REF_AV_FAC || '';

        if (record.facture) {
            ref = record.facture.REF_FAC || ref;
        } else if (record.avoir) {
            ref = record.avoir.REF_AVR || ref;
        } else if (record.surplus) {
            ref = record.surplus.REF_SURPLUS || ref;
        } else if (record.currentRef) {
            ref = record.currentRef;
        }

        // Use DATEVALID from related records
        const dateValid = getDateValid(record);
        const date = formatDate(dateValid);

        rowsHtml += `
            <tr>
                <td class="reference-col">${ref}</td>
                <td class="date-col">${date}</td>
                <td class="mnt-ttc-col text-right">${formatNumber(mntTtc)}</td>
                <td class="mnt-regler-col text-right">${formatNumber(mntRegler)}</td>
                <td class="solde-col text-right">${formatNumber(solde)}</td>
            </tr>`;
    });

    // Generate single table with all records
    let sectionsHtml = '';
    
    if (unpaidRecords.length > 0) {
        sectionsHtml = `
            <table>
                <thead>
                    <tr>
                        <th class="reference-col">Référence</th>
                        <th class="date-col">Date</th>
                        <th class="mnt-ttc-col">Mnt ttc</th>
                        <th class="mnt-regler-col">Mnt régler</th>
                        <th class="solde-col">Solde</th>
                    </tr>
                </thead>
                <tbody>
                    ${rowsHtml}
                    <tr class="grand-total-row">
                        <td colspan="2" style="text-align: right; font-weight: bold;">TOTAL :</td>
                        <td class="text-right">${formatNumber(grandTotalMntTtc)}</td>
                        <td class="text-right">${formatNumber(grandTotalMntRegler)}</td>
                        <td class="text-right">${formatNumber(grandTotalSolde)}</td>
                    </tr>
                </tbody>
            </table>
        `;
    }

    return sectionsHtml;
};

// Fetch unified records for a client
const fetchUnifiedRecords = async (codeClt) => {
    try {
        const response = await axios.get(`${API_URL}/reglement/getAllUnified/${codeClt}`);
        return response.data || [];
    } catch (error) {
        console.error('Error fetching unified records:', error);
        throw new Error(`Failed to fetch unified records: ${error.message}`);
    }
};

// Fetch client info
const fetchClientInfo = async (codeClt) => {
    try {
        const response = await axios.get(`${API_URL}/client/getclientbycodeclt/${codeClt}`);
        return response.data || {};
    } catch (error) {
        console.error('Error fetching client info:', error);
        return {};
    }
};

// Generate PDF blob
const generatePdfBlob = async (htmlString) => {
    const container = document.createElement('div');
    container.style.width = '210mm';
    container.style.position = 'absolute';
    container.style.left = '-10000px';
    container.innerHTML = htmlString;
    document.body.appendChild(container);
    
    try {
        const pdf = new jsPDF('l', 'mm', 'a5');
        const margin = 10;
        
        const page = container;
        page.style.width = '210mm';
        page.style.height = '148mm';
        page.style.padding = `${margin}mm`;
        page.style.boxSizing = 'border-box';
        page.style.backgroundColor = '#FFFFFF';

        const canvas = await html2canvas(page, {
            scale: 3,
            useCORS: true,
            logging: false,
            letterRendering: true,
            backgroundColor: '#FFFFFF',
            width: page.offsetWidth,
            height: page.offsetHeight,
        });

        const imgData = canvas.toDataURL('image/jpeg', 1.0);
        pdf.addImage(imgData, 'JPEG', 0, 0, 210, 148);
        
        return pdf.output('blob');
    } finally {
        document.body.removeChild(container);
    }
};

// Main print function
export const printReleveExterne = async (facture) => {
    try {
        // Create loading modal
        const loadingModalDiv = document.createElement('div');
        document.body.appendChild(loadingModalDiv);
        
        const closeLoadingModal = () => {
            ReactDOM.unmountComponentAtNode(loadingModalDiv);
            document.body.removeChild(loadingModalDiv);
        };
        
        ReactDOM.render(
            React.createElement(Modal, {
                title: "Génération du PDF",
                open: true,
                closable: true,
                maskClosable: false,
                footer: [
                    React.createElement(Button, {
                        key: "exit",
                        onClick: closeLoadingModal
                    }, "Sortir")
                ]
            }, React.createElement('div', {
                style: { display: 'flex', justifyContent: 'center', padding: '2rem' }
            }, React.createElement(Spin, {
                indicator: React.createElement(LoadingOutlined, { style: { fontSize: 24 }, spin: true }),
                tip: "Préparation du document..."
            }))),
            loadingModalDiv
        );

        // Fetch data
        const codeClt = facture.CODE_CLT || facture.code_clt;
        if (!codeClt) {
            throw new Error('Code client manquant');
        }

        const [unifiedRecords, clientInfo] = await Promise.all([
            fetchUnifiedRecords(codeClt),
            fetchClientInfo(codeClt)
        ]);

        // Generate sections
        const sectionsHtml = generateReleveSections(unifiedRecords, clientInfo);
        
        // Current date
        const currentDate = formatFullDate(new Date());
        
        // Format client name and code for header
        const clientName = clientInfo?.CLIENT || '';
        const clientCode = clientInfo?.COMPTE ? ` (${clientInfo.COMPTE})` : '';
        const clientDisplay = clientName + clientCode;
        
        // Generate HTML
        const html = processTemplate(releveExterneTemplate, {
            CLIENT_NAME: clientDisplay,
            CURRENT_DATE: currentDate,
            SECTIONS: sectionsHtml,
            PAGE_NUM: '1',
            TOTAL_PAGES: '1'
        });

        // Generate PDF
        const pdfBlob = await generatePdfBlob(html);
        
        // Close loading modal
        closeLoadingModal();

        // Create preview modal
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const previewModalDiv = document.createElement('div');
        document.body.appendChild(previewModalDiv);
        
        const closePreviewModal = () => {
            URL.revokeObjectURL(pdfUrl);
            ReactDOM.unmountComponentAtNode(previewModalDiv);
            document.body.removeChild(previewModalDiv);
        };
        
        ReactDOM.render(
            React.createElement(Modal, {
                title: `Aperçu PDF - Relevé Externe`,
                open: true,
                width: "90%",
                centered: true,
                closable: true,
                maskClosable: true,
                footer: [
                    React.createElement(Button, {
                        key: "exit",
                        onClick: closePreviewModal
                    }, "Sortir")
                ],
                onCancel: closePreviewModal
            }, React.createElement('div', {
                className: "pdf-preview-container"
            }, React.createElement('iframe', {
                src: pdfUrl,
                style: { width: '100%', height: '80vh', border: 'none' },
                title: "PDF Preview"
            }))),
            previewModalDiv
        );

        return {
            cleanup: closePreviewModal,
            pdfUrl
        };
    } catch (error) {
        console.error('Error generating relevé externe:', error);
        Modal.error({
            title: 'Erreur',
            content: `Échec de la génération du document: ${error.message}`,
        });
        throw new Error(`Failed to generate document: ${error.message}`);
    }
};

// Direct print function
export const printReleveExterneDirect = async (facture) => {
    try {
        // Fetch data
        const codeClt = facture.CODE_CLT || facture.code_clt;
        if (!codeClt) {
            throw new Error('Code client manquant');
        }

        const [unifiedRecords, clientInfo] = await Promise.all([
            fetchUnifiedRecords(codeClt),
            fetchClientInfo(codeClt)
        ]);

        // Generate sections
        const sectionsHtml = generateReleveSections(unifiedRecords, clientInfo);
        
        // Current date
        const currentDate = formatFullDate(new Date());
        
        // Format client name and code for header
        const clientName = clientInfo?.CLIENT || '';
        const clientCode = clientInfo?.COMPTE ? ` (${clientInfo.COMPTE})` : '';
        const clientDisplay = clientName + clientCode;
        
        // Generate HTML
        const html = processTemplate(releveExterneTemplate, {
            CLIENT_NAME: clientDisplay,
            CURRENT_DATE: currentDate,
            SECTIONS: sectionsHtml,
            PAGE_NUM: '1',
            TOTAL_PAGES: '1'
        });

        // Create iframe for printing
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position: absolute; width: 0; height: 0; left: -9999px; border: 0;';
        document.body.appendChild(iframe);
        
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.open();
        doc.write(html);
        doc.close();
        
        // Print after load
        iframe.onload = () => {
            setTimeout(() => {
                try {
                    iframe.contentWindow.focus();
                    iframe.contentWindow.print();
                    
                    setTimeout(() => {
                        document.body.removeChild(iframe);
                    }, 1000);
                } catch (err) {
                    console.error('Error in print process:', err);
                    document.body.removeChild(iframe);
                }
            }, 500);
        };
    } catch (error) {
        console.error('Error generating relevé externe for direct print:', error);
        throw new Error(`Failed to generate document: ${error.message}`);
    }
};

